import { type JsonObject } from "@bufbuild/protobuf";
import { AssetType, PresentationMode as SuitePresentationMode, type AssetRef } from "#gen/bundles/v1/bundle_pb.js";
import type { Page } from "#gen/pages/v2/page_pb.js";
import { objectValue, stringValue, structValue } from "#lib/struct.js";

// Reserved page level key inside page.config. Component config is keyed by
// component id, which always ends in a numeric suffix, so this namespace can
// never collide with a real component. Exit button styling lives here.
const PAGE_CONFIG_KEY = "__page";

export type PresentationMode = "page" | "tray";

export interface ExitButtonStyle {
	color?: string;
	iconColor?: string;
	borderColor?: string;
}

// Defaults for the tray exit button, shared so TrayExitButton and the
// PageAppearanceEditor pickers stay in sync. Hex so they work as CSS and in the
// color picker, which only takes hex.
export const DEFAULT_EXIT_BUTTON_FILL = "#ffffffbd";
export const DEFAULT_EXIT_BUTTON_BORDER = "#ffffff";
export const DEFAULT_EXIT_BUTTON_ICON = "#111928";

// getPresentationMode returns how a page presents when navigated to, read from
// the suite AssetRef. Unspecified means Tray, which keeps legacy pages Tray.
export function getPresentationMode(ref: AssetRef | undefined): PresentationMode {
	return ref?.presentationMode === SuitePresentationMode.PAGE ? "page" : "tray";
}

// toPresentationMode maps the app string onto the proto enum. Tray is stored as
// UNSPECIFIED so a page reverted to Tray matches a legacy never set AssetRef.
export function toPresentationMode(mode: PresentationMode): SuitePresentationMode {
	return mode === "page" ? SuitePresentationMode.PAGE : SuitePresentationMode.UNSPECIFIED;
}

export function getExitButtonStyle(config: JsonObject | undefined): ExitButtonStyle {
	const exit = objectValue(objectValue(config, PAGE_CONFIG_KEY), "exitButton");
	return { color: stringValue(exit, "color"), iconColor: stringValue(exit, "iconColor"), borderColor: stringValue(exit, "borderColor") };
}

export function setExitButtonColor(page: Page, color: string | undefined): void {
	setExitButtonValue(page, "color", color);
}

export function setExitButtonIconColor(page: Page, iconColor: string | undefined): void {
	setExitButtonValue(page, "iconColor", iconColor);
}

export function setExitButtonBorderColor(page: Page, borderColor: string | undefined): void {
	setExitButtonValue(page, "borderColor", borderColor);
}

// presentsAsFullPage decides at runtime whether a navigated asset should
// replace the view like a web page instead of opening in the tray. Only PAGE_V2
// AssetRefs explicitly set to "page" qualify.
export function presentsAsFullPage(ref: AssetRef | undefined): boolean {
	return ref?.type === AssetType.PAGE_V2 && ref.presentationMode === SuitePresentationMode.PAGE;
}

function setExitButtonValue(page: Page, key: keyof ExitButtonStyle, value: string | undefined): void {
	if (value) {
		ensureExitButton(page)[key] = value;
	} else {
		const exit = structValue(structValue(page.config, PAGE_CONFIG_KEY), "exitButton");
		if (exit) {
			delete exit[key];
		}
		pruneEmpty(page);
	}
}

// ensurePageConfig returns a mutable page.config["__page"] object, creating the
// config map and the reserved object as needed (struct.setValue is shallow and
// won't create intermediate objects).
function ensurePageConfig(page: Page): JsonObject {
	page.config ??= {};
	let pageConfig = structValue(page.config, PAGE_CONFIG_KEY);
	if (!pageConfig) {
		pageConfig = {};
		page.config[PAGE_CONFIG_KEY] = pageConfig;
	}
	return pageConfig;
}

function ensureExitButton(page: Page): JsonObject {
	const pageConfig = ensurePageConfig(page);
	let exit = structValue(pageConfig, "exitButton");
	if (!exit) {
		exit = {};
		pageConfig.exitButton = exit;
	}
	return exit;
}

// pruneEmpty drops emptied reserved objects so removing the last setting leaves
// no residual bytes.
function pruneEmpty(page: Page): void {
	if (!page.config) {
		return;
	}
	const pageConfig = structValue(page.config, PAGE_CONFIG_KEY);
	if (pageConfig) {
		const exit = structValue(pageConfig, "exitButton");
		if (exit && Object.keys(exit).length === 0) {
			delete pageConfig.exitButton;
		}
		if (Object.keys(pageConfig).length === 0) {
			delete page.config[PAGE_CONFIG_KEY];
		}
	}
	if (Object.keys(page.config).length === 0) {
		page.config = undefined;
	}
}
